import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminParty } from '../../../../../Store/Store/Party/AdminParty';
import { PARTY_STATUS_CONFIG } from '../../../../../helper/storeQuotation';

const SendPartiesModal = ({ quotation, onSave, onClose }) => {

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAdminParty({ storeType: "1", is_main: true }));
  }, [dispatch])

  const partyData = useSelector((state) => state?.getAdminParty?.user?.data)

  const existingIds = quotation.parties.map(p => typeof p.party_id === 'object' ? p.party_id?._id : p.party_id);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleSelection = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredNotified = useMemo(() => {
    const parties = Array.isArray(quotation.parties) ? quotation.parties : [];
    if (!searchTerm) return parties;
    const term = searchTerm.toLowerCase();
    return parties.filter(p =>
      (p.party_snapshot?.name || "").toLowerCase().includes(term) ||
      (p.party_snapshot?.city || "").toLowerCase().includes(term) ||
      (p.party_snapshot?.phone || "").includes(term)
    );
  }, [quotation.parties, searchTerm]);

  const filteredParties = useMemo(() => {
    const data = Array.isArray(partyData) ? partyData : [];
    const available = data.filter(p => !existingIds.includes(p._id));
    if (!searchTerm) return available;
    const term = searchTerm.toLowerCase();
    return available.filter(p =>
      (p?.name || "").toLowerCase().includes(term)
    );
  }, [existingIds, partyData, searchTerm]);

  return (
    <Modal
      show={true}
      onHide={onClose}
      centered
      backdrop="static"
      size="md"
      contentClassName="border-0 shadow-lg rounded-4 overflow-hidden"
    >
      <Modal.Header closeButton className="bg-white border-bottom-0 pt-4 px-4 pb-2">
        <div>
          <Modal.Title className="fw-bold h5 mb-1" style={{ color: '#1e293b' }}>
            {quotation.parties.length === 0 ? "Send to Parties" : "Add More Parties"}
          </Modal.Title>
          <div className="text-muted" style={{ fontSize: '13px' }}>
            Step 2 — Select parties to send this enquiry
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 pb-4">
        {/* Search Bar */}
        <div className="mb-4 mt-2">
          <InputGroup className="bg-light rounded-3 border-0 overflow-hidden shadow-none" style={{ border: '1px solid #e2e8f0 !important' }}>
            <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
              <i className="fa fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by name..."
              className="bg-transparent border-0 py-2 shadow-none"
              style={{ fontSize: '14px' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button variant="link" className="text-muted p-2" onClick={() => setSearchTerm("")}>
                <i className="fa fa-times-circle"></i>
              </Button>
            )}
          </InputGroup>
        </div>

        {/* Selected Counter/Toolbar */}
        {selectedIds.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mb-3 p-2 rounded-3" style={{ backgroundColor: '#fff8efff', border: '1px solid #feeedbff' }}>
            <span className="small fw-semibold text-primary ms-2 d-flex align-items-center">
              <i className="fa fa-users me-2"></i>{selectedIds.length} parties selected
            </span>
            <Button
              variant="link"
              size="sm"
              className="text-decoration-none text-muted p-0 me-2 small fw-medium"
              onClick={() => setSelectedIds([])}
            >
              Clear All
            </Button>
          </div>
        )}

        <div className="custom-scroll" style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '4px' }}>

          {/* Already Sent Section */}
          <div className="d-flex flex-column gap-2 mb-3">
            {filteredNotified.map(p => {
              const pId = typeof p.party_id === 'object' ? p.party_id?._id : p.party_id;
              const statusCfg = PARTY_STATUS_CONFIG[p.party_status];

              return (
                <div
                  key={pId}
                  className="py-1 px-3 rounded-3 border d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor: '#f8fafc',
                    borderColor: '#e2e8f0',
                  }}
                >
                  {/* LEFT SIDE */}
                  <div className="d-flex flex-column">
                    <div className="fw-bold text-dark" style={{ fontSize: '12px' }}>
                      {p.party_snapshot?.name}
                    </div>
                  </div>

                  {/* RIGHT SIDE (STATUS) */}
                  <div
                    className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                    style={{
                      backgroundColor: statusCfg ? `${statusCfg.color}22` : '#ecfdf5',
                      color: statusCfg?.color || '#16a34a',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    <i className={`fa ${statusCfg ? 'fa-circle' : 'fa-check-circle'}`} style={{ fontSize: '8px' }}></i>
                    {statusCfg?.label || 'Sent'}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="small fw-bold text-uppercase text-muted mb-0 opacity-75 d-flex align-items-center" style={{ letterSpacing: '0.05em', fontSize: '11px' }}>
              <i className="fa fa-list-ul me-2 mb-1"></i> {searchTerm ? "Search Results (New)" : "Select New Parties"} ({filteredParties.length})
            </h6>
            {filteredParties.length > 0 && (
              <Button
                variant="link"
                size="sm"
                className="text-decoration-none p-0 small fw-bold"
                style={{ fontSize: '11px', color: '#ff6700' }}
                onClick={() => {
                  const allIds = filteredParties.map(p => p._id);
                  setSelectedIds(prev => Array.from(new Set([...prev, ...allIds])));
                }}
              >
                SELECT ALL
              </Button>
            )}
          </div>

          {filteredParties.length === 0 ? (
            <div className="text-center py-5 border rounded-4 bg-light bg-opacity-50 border-dashed">
              <div className="mb-3">
                <i className="fa fa-users text-muted opacity-25" style={{ fontSize: '40px' }}></i>
              </div>
              <div className="text-muted small fw-medium">
                {searchTerm ? "No matching parties found" : "All parties already included"}
              </div>
              {searchTerm && (
                <Button variant="link" size="sm" className="mt-2 text-decoration-none fw-bold" onClick={() => setSearchTerm("")}>
                  Clear Search Filter
                </Button>
              )}
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {filteredParties.map(p => {
                const isSelected = selectedIds.includes(p._id);
                return (
                  <div
                    key={p._id}
                    onClick={() => toggleSelection(p._id)}
                    className={`p-3 rounded-3 border d-flex justify-content-between align-items-center transition-all ${isSelected
                      ? ''
                      : 'hover-bg-light border-light-subtle bg-white'
                      }`}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor: isSelected ? '#fff7efff' : '#fff',
                      borderColor: isSelected ? '#ff6700' : '#f9f4f1ff',
                      borderWidth: '1.5px',
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className={`p-1 rounded-circle border d-flex align-items-center justify-content-center transition-all 
                      ${isSelected ? 'text-white border-2 scale-110' : 'bg-white border-secondary'
                        }`} style={isSelected ? { backgroundColor: "#ff6700", border: "2px solid #ff6700", width: '22px', height: '22px' } : { width: '22px', height: '22px' }}>
                        {isSelected && <i className="fa fa-check" style={{ fontSize: '10px' }}></i>}
                      </div>
                      <div>
                        <div className={`fw-bold mb-0 ${isSelected ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '14px', letterSpacing: '-0.01em' }}>{p.name}</div>
                        <div className="text-muted " style={{ fontSize: '13px' }}>{p.email}</div>
                        <div className="text-muted d-flex align-items-center gap-2 mt-2" style={{ fontSize: '12px', }}>
                          <span className="d-flex align-items-center gap-1"><i className="fa fa-phone small opacity-50"></i> {p.phone}</span>
                          <span className="opacity-25 fs-6">|</span>
                          <span className="text-uppercase fw-bold d-flex align-items-center gap-1" style={{ fontSize: '10px' }}><i className="fa fa-map-marker small opacity-50"></i> {p.city}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light border-top-0 px-4 py-3 justify-content-between">
        <Button
          variant="secondary"
          className="text-decoration-none text-white px-3 fw-bold px-0 shadow-none"
          size="sm"
          onClick={onClose}
        >
          Cancel
        </Button>
        <button
          disabled={selectedIds.length === 0}
          className='btn btn-primary px-3 btn-md'
          style={{ fontWeight: "600" }}
          onClick={() => onSave(selectedIds)}>
          {selectedIds.length > 0 ? `Send to ${selectedIds.length} Parties` : "Select Parties"}
        </button>

      </Modal.Footer>

      <style>{`
        .hover-bg-light:hover { background-color: #fcfaf8ff !important; border-color: #e1d7cbff !important; transform: translateY(-1px); }
        .transition-all { transition: all 0.2s ease; }
        .custom-scroll::-webkit-scrollbar { width: 5px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #f0eae2ff; border-radius: 10px; }
        .custom-scroll::-webkit-scrollbar-thumb:hover { background: #e1d9cbff; }
        .border-dashed { border-style: dashed !important; }
        .scale-110 { transform: scale(1.1); }
      `}</style>
    </Modal >
  );
};

export default SendPartiesModal;
